import React, { useEffect, useState, useCallback, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState } from "@/components/Common";
import { TagsField } from "@/components/TagsField";
import { EntitaAllegati } from "@/components/AllegatiPanel";
import { CATEGORIE_GENERICHE } from "@/lib/anagrafiche";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Search, Loader2, AlertTriangle, Paperclip } from "lucide-react";
import { toast } from "sonner";

const CF_KEYS = ["data_nascita", "sesso", "luogo_nascita", "provincia_nascita", "stato_nascita"];

function FormField({ f, form, setForm, relations, onCf }) {
  const set = (v) => setForm({ ...form, [f.name]: v });
  const tid = `field-${f.name}`;
  if (f.type === "textarea") return <Textarea value={form[f.name] ?? ""} onChange={(e) => set(e.target.value)} className="mt-1" data-testid={tid} />;
  if (f.type === "tags") return <TagsField value={form[f.name]} onChange={set} optionKey={f.optionKey} testid={tid} />;
  if (f.type === "custom") { const C = f.component; return <C value={form[f.name]} onChange={set} />; }
  if (f.type === "checks") {
    const cur = Array.isArray(form[f.name]) ? form[f.name] : [];
    return (
      <div className="mt-2 flex flex-wrap gap-4" data-testid={tid}>
        {f.options.map((o) => (
          <label key={o.value} className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" className="h-4 w-4 accent-[#E11D48]" checked={cur.includes(o.value)}
              onChange={(e) => set(e.target.checked ? [...cur, o.value] : cur.filter((x) => x !== o.value))}
              data-testid={`${tid}-${o.value}`} />
            {o.label}
          </label>
        ))}
      </div>
    );
  }
  if (f.type === "select" || f.type === "relation") {
    const opts = f.type === "select" ? f.options.map((o) => ({ v: o.value ?? o, l: o.label ?? o }))
      : (relations[f.name] || []).map((o) => ({ v: o.id, l: f.relationLabel(o) }));
    return (
      <Select value={form[f.name] ?? ""} onValueChange={set}>
        <SelectTrigger className="mt-1" data-testid={tid}><SelectValue placeholder="Seleziona..." /></SelectTrigger>
        <SelectContent>{opts.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
      </Select>
    );
  }
  return (
    <Input type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
      value={form[f.name] ?? ""}
      onChange={(e) => (f.name === "cf" && onCf ? onCf(e.target.value) : set(e.target.value))}
      className="mt-1" data-testid={tid} />
  );
}

// Generic anagrafica CRUD page.
export default function CrudPage({ title, subtitle, endpoint, columns, fields, testid, canCreate = true, cfAutofill, validate, detailRoute, filters = [], wide, allegati, mobileCards }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState(null);
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [flt, setFlt] = useState({});
  const [fltOpts, setFltOpts] = useState({});
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [relations, setRelations] = useState({});
  const [toDelete, setToDelete] = useState(null);
  const [backTo, setBackTo] = useState(null);
  const [allegatiRow, setAllegatiRow] = useState(null);

  const canEcon = user?.can_economics;
  const visible = useMemo(() => fields.filter((f) => !(f.econ && !canEcon)), [fields, canEcon]);

  const load = useCallback(async () => {
    const params = { ...(q ? { q } : {}), ...Object.fromEntries(Object.entries(flt).filter(([, v]) => v && v !== "__all")) };
    const { data } = await api.get(`/${endpoint}`, { params });
    setItems(data);
  }, [endpoint, q, flt]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setQ(searchParams.get("q") || ""); }, [searchParams]);

  useEffect(() => {
    const relFields = fields.filter((f) => f.type === "relation");
    Promise.all(
      relFields.map((f) => api.get(`/${f.relationEndpoint}`).then((r) => [f.name, r.data]))
    ).then((entries) => setRelations(Object.fromEntries(entries)));
  }, [fields]);

  useEffect(() => {
    if (open) return;
    filters.forEach((fl) => fl.optionKey && api.get(`/options/${fl.optionKey}`).then((r) => setFltOpts((o) => ({ ...o, [fl.name]: r.data.values }))));
  }, [filters, open]);

  const openNew = () => {
    const init = {};
    visible.forEach((f) => { if (f.type !== "heading") init[f.name] = f.default ?? (f.type === "tags" ? [] : ""); });
    setForm(init);
    setEditing(null);
    setOpen(true);
  };

  const openEdit = useCallback((row) => {
    const init = {};
    visible.forEach((f) => { if (f.type !== "heading") init[f.name] = row[f.name] ?? (f.type === "tags" ? [] : f.default ?? ""); });
    setForm(init);
    setEditing(row);
    setOpen(true);
  }, [visible]);

  useEffect(() => {
    const eid = searchParams.get("edit");
    if (!eid || !items) return;
    const row = items.find((r) => r.id === eid);
    if (row) { openEdit(row); setBackTo(detailRoute ? `/${detailRoute}/${eid}` : null); }
    setSearchParams({}, { replace: true });
  }, [items, searchParams, setSearchParams, openEdit, detailRoute]);

  const onCf = async (val) => {
    const cf = val.toUpperCase();
    setForm((f) => ({ ...f, cf }));
    if (cf.length !== 16) return;
    try {
      const { data } = await api.get("/cf/decode", { params: { cf } });
      setForm((f) => ({ ...f, ...Object.fromEntries(CF_KEYS.map((k) => [k, data[k]])) }));
      toast.success("Dati compilati dal codice fiscale");
    } catch { toast.warning("Codice fiscale non valido: dati non compilati"); }
  };

  const warning = validate ? validate(form) : "";

  const rowActions = (row) => (
    <div className="flex items-center gap-1">
      {allegati && (
        <button onClick={() => setAllegatiRow(row)} className="flex items-center gap-0.5 rounded p-2.5 sm:p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-900" data-testid={`${endpoint}-allegati`} title="Allegati">
          <Paperclip className="h-4 w-4" /><span className="text-xs font-semibold" data-testid={`${endpoint}-allegati-count`}>{row.n_allegati || 0}</span>
        </button>
      )}
      {user?.can_write && (
        <>
          <button onClick={() => openEdit(row)} className="rounded p-2.5 sm:p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-900" data-testid={`${endpoint}-edit`}>
            <Pencil className="h-4 w-4" />
          </button>
          <button onClick={() => setToDelete(row)} className="rounded p-2.5 sm:p-1.5 text-slate-500 hover:bg-rose-50 hover:text-rose-600" data-testid={`${endpoint}-delete`}>
            <Trash2 className="h-4 w-4" />
          </button>
        </>
      )}
    </div>
  );

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      fields.forEach((f) => {
        if (f.type === "number" && payload[f.name] !== "" && payload[f.name] !== undefined) payload[f.name] = parseFloat(payload[f.name]);
      });
      if (editing) await api.put(`/${endpoint}/${editing.id}`, payload);
      else {
        const { data: created } = await api.post(`/${endpoint}`, payload);
        if (allegati) {
          setEditing(created);
          toast.success("Elemento creato: ora puoi caricare gli allegati");
          load();
          return;
        }
      }
      toast.success(editing ? "Modifiche salvate" : "Elemento creato");
      if (warning) toast.warning(warning);
      setOpen(false);
      if (backTo) { navigate(backTo); return; }
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/${endpoint}/${toDelete.id}`);
      toast.success("Elemento eliminato");
      setToDelete(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid={testid}>
      <PageHeader title={title} subtitle={subtitle}>
        {canCreate && user?.can_write && (
          <Button onClick={openNew} data-testid={`${endpoint}-new-btn`} className="bg-[#E11D48] hover:bg-[#BE123C]">
            <Plus className="mr-1.5 h-4 w-4" /> Nuovo
          </Button>
        )}
      </PageHeader>

      <div className="mb-4 flex flex-col gap-3 sm:flex-row">
        <div className="relative w-full max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca..." className="pl-9" data-testid={`${endpoint}-search`} />
        </div>
        {filters.map((fl) => (
          <Select key={fl.name} value={flt[fl.name] || "__all"} onValueChange={(v) => setFlt({ ...flt, [fl.name]: v })}>
            <SelectTrigger className="w-full sm:w-56" data-testid={`filter-${fl.name}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">{fl.label}: {fl.allLabel || "tutte"}</SelectItem>
              {(fl.options || (fltOpts[fl.name] || []).map((o) => ({ value: o, label: o }))).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        ))}
      </div>

      <div className="rounded-xl border bg-white overflow-hidden">
        {items === null ? (
          <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : items.length === 0 ? (
          <EmptyState title="Nessun elemento" hint="Crea il primo elemento con il pulsante Nuovo." />
        ) : (
          <>
          {mobileCards && (
            <div className="divide-y sm:hidden" data-testid={`${endpoint}-cards`}>
              {items.map((row) => (
                <div key={row.id} data-testid={`${endpoint}-card`} className={`p-4 ${detailRoute ? "cursor-pointer active:bg-slate-50" : ""}`}
                  onClick={detailRoute ? () => navigate(`/${detailRoute}/${row.id}`) : undefined}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 text-base">{columns[0].render ? columns[0].render(row) : row[columns[0].key]}</div>
                    <div onClick={(e) => e.stopPropagation()}>{rowActions(row)}</div>
                  </div>
                  <dl className="mt-2 grid grid-cols-1 gap-1 text-sm">
                    {columns.slice(1).map((c) => (
                      <div key={c.key} className="flex items-start justify-between gap-3">
                        <dt className="shrink-0 text-xs uppercase tracking-wide text-slate-400">{c.label}</dt>
                        <dd className="min-w-0 text-right text-slate-700 break-words">{c.render ? c.render(row) : row[c.key] ?? "—"}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              ))}
            </div>
          )}
          <div className={`overflow-x-auto ${mobileCards ? "hidden sm:block" : ""}`}>
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  {columns.map((c) => <TableHead key={c.key} className="text-xs uppercase tracking-wider text-slate-500">{c.label}</TableHead>)}
                  <TableHead className={allegati ? "w-32" : "w-24"} />
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((row) => (
                  <TableRow key={row.id} data-testid={`${endpoint}-row`}
                    className={detailRoute ? "cursor-pointer" : ""}
                    onClick={detailRoute ? () => navigate(`/${detailRoute}/${row.id}`) : undefined}>
                    {columns.map((c) => <TableCell key={c.key} className="text-sm">{c.render ? c.render(row) : row[c.key] ?? "—"}</TableCell>)}
                    <TableCell onClick={(e) => e.stopPropagation()}>{rowActions(row)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          </>
        )}
      </div>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setBackTo(null); if (allegati) load(); } }}>
        <DialogContent className={`${wide ? "max-w-5xl" : "max-w-2xl"} max-h-[90vh] overflow-y-auto`}>
          <DialogHeader>
            <DialogTitle>{editing ? "Modifica" : "Nuovo"} — {title}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            {visible.filter((f) => !f.showIf || f.showIf(form)).map((f) => f.type === "heading" ? (
              <h4 key={f.name} className="sm:col-span-2 mt-2 border-b border-slate-100 pb-1.5 font-display text-sm font-semibold text-[#E11D48]" data-testid={`section-${f.name}`}>{f.label}</h4>
            ) : (
              <div key={f.name} className={f.full ? "sm:col-span-2" : ""}>
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">{f.label}</Label>
                <FormField f={f} form={form} setForm={setForm} relations={relations} onCf={cfAutofill ? onCf : null} />
                {f.hint && <p className="mt-1 text-xs text-slate-400">{f.hint}</p>}
              </div>
            ))}
            {warning && (
              <div className="sm:col-span-2 flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-800" data-testid="form-warning">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /> {warning}
              </div>
            )}
            {allegati && (
              <div className="sm:col-span-2 mt-2 border-t border-slate-100 pt-4">
                {editing ? (
                  <EntitaAllegati entita={endpoint} id={editing.id} canWrite={user?.can_write} categorie={CATEGORIE_GENERICHE} />
                ) : (
                  <p className="flex items-center gap-2 text-sm text-slate-400" data-testid="allegati-hint"><Paperclip className="h-4 w-4" />Gli allegati si caricano dopo il primo salvataggio.</p>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} disabled={saving} className="bg-[#E11D48] hover:bg-[#BE123C]" data-testid="save-btn">
              {saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />} Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!allegatiRow} onOpenChange={(o) => { if (!o) { setAllegatiRow(null); load(); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="allegati-dialog">
          <DialogHeader><DialogTitle>Allegati — {allegatiRow && (allegatiRow.denominazione || allegatiRow.titolo || allegatiRow.titolo_piano || allegatiRow.nome_piano || `${allegatiRow.cognome || ""} ${allegatiRow.nome || ""}`.trim() || allegatiRow.descrizione || title)}</DialogTitle></DialogHeader>
          {allegatiRow && <EntitaAllegati entita={endpoint} id={allegatiRow.id} canWrite={user?.can_write} categorie={CATEGORIE_GENERICHE} />}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare questo elemento?</AlertDialogTitle>
            <AlertDialogDescription>L'elemento verrà disattivato (soft delete), lo storico resta consultabile.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-delete">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
